import { json, redirect } from "@remix-run/node"
import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/node"
import { Link, useLoaderData } from "@remix-run/react"
import Header from "~/components/header"
import { Public } from "~/utils/remix"
import Typography from "~/components/typography"
import { FieldArray, useControlField, useField, useIsSubmitting, ValidatedForm, validationError } from "remix-validated-form"
import { withZod } from "@remix-validated-form/with-zod"
import { z } from "zod"
import { Cross1Icon, PlusIcon, InfoCircledIcon, ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible"
import { Button } from "~/components/ui/button"
import { TrackEvent, EventType } from "~/models/events.server"
import {
  FormCheckbox,
  FormDate,
  FormFile,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormText,
} from "~/components/form-fields"
import { ContentBlock } from "~/components/cards"
import { useEffect, useRef, useState } from "react"
import { Countries } from "~/utils/data"
import { imageJSON, videoJSON } from "~/utils/image.server"
import { EnqueueProcessVideo } from "~/workers/handlers.server"
import { cn } from "~/utils/shadecn"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "~/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group"

export const meta: MetaFunction = () => {
  return [
    { title: `Share Your Real World Sex (and make money from making love, not porn)` },
    { property: "og:title", content: `Share Your Real World Sex (and make money from making love, not porn)` },
    {
      name: "description",
      content: `Join our diverse Make Love Not Pornstar community. Share your homemade, real world sex videos, and earn real world cash. Your sex videos are safe and supported by our curators.`,
    },
  ]
}

const validator = withZod(
  z.object({
    FirstName: z.string().min(1, { message: "First name is required" }),
    LastName: z.string().min(1, { message: "Last name is required" }),
    NickName: z.string().optional(),
    Birthday: z.coerce.date({
      errorMap: (issue, { defaultError }) => ({
        message: issue.code === "invalid_date" ? "Birthday is required" : defaultError,
      }),
    }),
    Country: z.string().min(1, { message: "Country is required" }),
    IDImageOne: z.string().trim().url({ message: "ID Photo 1 is required" }),
    IDImageTwo: z.string().trim().url({ message: "ID Photo 2 is required" }),
    Participants: z
      .array(
        z.object({
          ParticipantID: z.coerce.number().optional(),
          FirstName: z.string().min(1, { message: "First name is required" }),
          LastName: z.string().min(1, { message: "Last name is required" }),
          Email: z.string().email({ message: "Invalid email format" }).min(1, { message: "Email is required" }),
          NickName: z.string().optional(),
          Birthday: z.coerce.date({ required_error: "Date of birth is required" }),
          Country: z.string().min(1, { message: "Country is required" }),
          IDImageOne: z.string().trim().url({ message: "ID Photo 1 is required" }),
          IDImageTwo: z.string().trim().url({ message: "ID Photo 2 is required" }),
        })
      )
      .optional(),
    VideoTitle: z.string().min(1, { message: "Video title is required" }),
    VideoDescription: z.string().min(1, { message: "Video description is required" }),
    VideoFilm: z.coerce.date({
      errorMap: (issue, { defaultError }) => ({
        message: issue.code === "invalid_date" ? "Video filmed date is required" : defaultError,
      }),
    }),
    VideoTags: z.array(z.string().min(1, { message: "Video tags are required" })).optional(),
    VideoIntro: z.string().trim().url({ message: "Video intro is required" }),
    VideoIntroDuration: z.coerce.number().min(30, { message: "Video must be at least 30 seconds long" }),
    VideoReal: z.string().trim().url({ message: "Video is required" }),
    VideoRealDuration: z.coerce.number().min(180, { message: "Video must be at least 3 minutes long" }),
    MainStill: z.string().trim().url({ message: "Main still is required" }),
    AdditionalStill1: z.union([z.literal(""), z.string().trim().url()]),
    AdditionalStill2: z.union([z.literal(""), z.string().trim().url()]),
    AdditionalStill3: z.union([z.literal(""), z.string().trim().url()]),
    AdditionalStill4: z.union([z.literal(""), z.string().trim().url()]),
    Exclusivity: z.coerce
      .boolean({ required_error: "You must agree this video is exclusive" })
      .refine((value) => value === true, { message: "You must agree this video is exclusive" }),
    ContainsMusic: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(["yes", "no"], { required_error: "Please let us know if your video contains music" })
    ),
    MusicSource: z.string().optional(),
    Agreement: z.coerce
      .boolean({ required_error: "You must agree to the video submission agreement" })
      .refine((value) => value === true, { message: "You must agree to the video submission agreement" }),
  }).superRefine((data, ctx) => {
    if (data.ContainsMusic === "yes" && (!data.MusicSource || data.MusicSource.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please tell us where you sourced the music",
        path: ["MusicSource"],
      })
    }
  })
)

export const action = Public(async ({ request, context }: ActionFunctionArgs) => {
  const db = context.db
  const user = context.user!

  const result = await validator.validate(await request.formData())
  if (result.error) {
    return validationError(result.error)
  }

  var participants = []

  if (result.data.Participants) {
    participants = await Promise.all(
      result.data.Participants?.map(async (participant) => {
        return db.videoParticipants.create({
          data: {
            UserID: user.UserID,
            CognitoUserID: user.CognitoUserID,
            FirstName: participant.FirstName,
            LastName: participant.LastName,
            Email: participant.Email,
            NickName: participant.NickName,
            Birthday: participant.Birthday,
            Country: participant.Country,
            IDImageOne: JSON.stringify(imageJSON(participant.IDImageOne)),
            IDImageTwo: JSON.stringify(imageJSON(participant.IDImageTwo)),
          },
        })
      })
    )
  }

  const participantIDs = participants.map((participant) => {
    return participant.ParticipantID
  })

  const stills = [imageJSON(result.data.MainStill)]
  if (result.data.AdditionalStill1) stills.push(imageJSON(result.data.AdditionalStill1))
  if (result.data.AdditionalStill2) stills.push(imageJSON(result.data.AdditionalStill2))
  if (result.data.AdditionalStill3) stills.push(imageJSON(result.data.AdditionalStill3))
  if (result.data.AdditionalStill4) stills.push(imageJSON(result.data.AdditionalStill4))

  const video = await db.videos.create({
    data: {
      UserID: user.UserID,
      CognitoUserID: user.CognitoUserID,
      FirstName: result.data.FirstName,
      LastName: result.data.LastName,
      NickName: result.data.NickName,
      Birthday: result.data.Birthday,
      Country: result.data.Country,
      IDImageOne: JSON.stringify(imageJSON(result.data.IDImageOne)),
      IDImageTwo: JSON.stringify(imageJSON(result.data.IDImageTwo)),
      Participants: JSON.stringify(participantIDs),
      VideoTitle: result.data.VideoTitle,
      VideoDescription: result.data.VideoDescription,
      VideoFilm: result.data.VideoFilm,
      VideoIntro: JSON.stringify(videoJSON(result.data.VideoIntro)),
      VideoReal: JSON.stringify(videoJSON(result.data.VideoReal)),
      VideoDuration: result.data.VideoRealDuration,
      Stills: JSON.stringify(stills),
      ContainsMusic: result.data.ContainsMusic === "yes",
      MusicSource: result.data.ContainsMusic === "yes" ? result.data.MusicSource ?? null : null,
      Status: 0,
    },
  })

  await EnqueueProcessVideo(context, { VideoID: video.VideoID })

  if (result.data.VideoTags && result.data.VideoTags.length > 0) {
    await db.videoMarqueeTags.createMany({
      data: result.data.VideoTags.map((tagID, index) => ({
        VideoID: video.VideoID,
        MarqueeTagID: parseInt(tagID),
        Sort: index + 1,
      })),
    })
  }

  await TrackEvent(context, EventType.SubmitVideo, { VideoID: video.VideoID!, VideoName: video.VideoTitle })

  return redirect("/dashboard/pending")
})

export const loader = Public(async ({ request, context }: LoaderFunctionArgs) => {
  const db = context.db
  const user = context.user || false

  const tags = await db.marqueeTags.findMany({
    where: { Deleted: 0, Status: 1 },
    orderBy: {
      Name: "asc",
    },
  })

  return json({ tags, user })
})

export default function VideoCreate() {
  const data = useLoaderData<typeof loader>()
  const [introUrl, setIntroUrl] = useState("")
  const [videoUrl, setVideoUrl] = useState("")
  const [mainStill, setMainStill] = useState("")
  const [still1, setStill1] = useState("")
  const [still2, setStill2] = useState("")
  const [still3, setStill3] = useState("")
  const [still4, setStill4] = useState("")
  const [openAdditionalInfo, setOpenAdditionalInfo] = useState(false)
  const [formDisabled, setFormDisabled] = useState(false)
  const [showStillsDialog, setShowStillsDialog] = useState(false)
  const [containsMusic, setContainsMusic] = useState<string>("")
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    setFormDisabled(!data.user)
  }, [data.user])

  useEffect(() => {
    if (document.cookie.indexOf("seenVideoSubmitAdditionalInfo=true") === -1) {
      setOpenAdditionalInfo(true)
    }
  }, [])

  function dismissAdditionalInfo() {
    document.cookie = "seenVideoSubmitAdditionalInfo=true"
  }

  function handleOpenChange(isOpen: boolean) {
    setOpenAdditionalInfo(isOpen)
    if (!isOpen) {
      dismissAdditionalInfo()
    }
  }

  return (
    <div className="max-w-5xl m-auto mt-16">
      <Header title="Share your Real World Sex Video" />
      <div className="mx-4 md:mx-16">
        <Collapsible open={openAdditionalInfo} onOpenChange={handleOpenChange} className="mb-8">
          <CollapsibleTrigger asChild>
            <button className="inline-flex items-center gap-2 text-foreground hover:underline">
              <InfoCircledIcon className="text-primary w-4 h-4" />
              <span className="text-sm">Learn more about becoming a MakeLoveNotPornstar</span>
              {openAdditionalInfo ?
                <ChevronUpIcon className="w-5 h-5" />
              : <ChevronDownIcon className="w-5 h-5" />}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="bg-card-secondary p-6 rounded-lg space-y-6 mt-4">
            <Typography>
              <h3>Everyone can make love, not porn</h3>
              <p>Everyone is sexy when they're having real world sex.</p>
              <p>We're not 'amateur', because 'amateur' implies the professionals are the only ones doing it right.</p>
              <p>
                Join our diverse community of real world people sharing what they do in bed in their everyday, real
                world lives, and inspire and educate our members to transform their own sexual attitudes and behaviors!
                You’ll be saving marriages and relationships, inspiring communications breakthroughs between couples,
                and making people feel better about their own real world bodies.
              </p>
            </Typography>
            <Typography>
              <h3>MakeLoveNotPorn is, as our name indicates, NOT porn</h3>
              <p>
                MakeLoveNotPorn showcases real world sex. We want you to share what really goes on in your bedroom (or
                wherever else you’re having the sex you really enjoy) - in all its funny messy wonderful glorious
                humanness.
              </p>
              <p>
                Your video is perfect for MLNP if it shows the sex you have in your everyday life naturally and
                spontaneously, without performing for the camera. Real world sex isn’t about a specific activity or
                mood. It’s about prioritizing your pleasure in the real world.
              </p>
              <p>
                Learn more by reading our{" "}
                <Link to="/makelovenotpornstar-resources" target="_blank">
                  MakeLoveNotPornstar Resources
                </Link>
                .
              </p>
            </Typography>
            <Typography>
              <h3>We're ethical - human eyes vet everything</h3>
              <ul className="marker:text-foreground">
                <li>Your video must be legal, consensual, and real</li>
                <li>
                  Everyone involved must be over 18 with the required ID verification
                  <ul className="marker:text-foreground">
                    <li>
                      We require two different, valid (non-expired) government issued IDs to be uploaded per
                      participant. This includes anyone on screen, and anyone present during the filming of the video,
                      even if they’re not on screen (someone filming for you, anyone else in the room, etc…){" "}
                    </li>
                    <li>
                      At least one of these IDS needs to have a photo. Some examples of acceptable government issued IDs
                      are:
                      <ul className="marker:text-foreground">
                        <li>Driver's license</li>
                        <li>Identity card</li>
                        <li>Passport</li>
                        <li>Birth certificate</li>
                        <li>Social security card</li>
                        <li>Marriage license</li>
                        <li>Military ID</li>
                        <li>Firearms license</li>
                        <li>Indigenous or tribal ID cards</li>
                        <li>Health cards (country specific)</li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li>
                  No copyright audio - this includes music, TV, radio, or any other audio not explicitly advertised as
                  copyright-free.
                </li>
                <li>No children, no poop, no animals</li>
                <li>Minimum intro length: 30 seconds</li>
                <li>Minimum full video length: 3 minutes</li>
              </ul>
            </Typography>
          </CollapsibleContent>
        </Collapsible>
        {!data.user && (
          <ContentBlock
            title="Ready to Share Your Love?"
            description="Join our community of real people sharing authentic real world sex moments. Create an account or log in to start sharing your content.">
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
              <Button variant="default" asChild>
                <Link to="/signup">Create an account</Link>
              </Button>
            </div>
          </ContentBlock>
        )}
        <ValidatedForm id="createVideo" className="space-y-8 mt-8" method="post" validator={validator} formRef={formRef}>
          <div className="space-y-4">
            <Typography>
              <h3 className={cn(formDisabled && "opacity-50")}>1. Tell Us a Bit About Yourself</h3>
            </Typography>
            <div className="space-y-4 px-1">
              <ParticipantForm namePrefix="" itemKey="User" label="Your Information" disabled={formDisabled} />
              <FieldArray name="Participants">
                {(items, { push, remove }) => (
                  <>
                    {items.map((item, index) => (
                      <ParticipantForm
                        key={index}
                        itemKey={item.key}
                        namePrefix={`Participants[${index}].`}
                        label={`Additional Participant #${index + 1}`}
                        remove={() => remove(index)}
                        disabled={formDisabled}
                      />
                    ))}
                    <Button
                      onClick={(e) => {
                        push({})
                        e.preventDefault()
                      }}
                      disabled={formDisabled}>
                      <PlusIcon /> &nbsp;Add Participant
                    </Button>
                  </>
                )}
              </FieldArray>
            </div>
          </div>
          <div className="space-y-4">
            <Typography>
              <h3 className={cn(formDisabled && "opacity-50")}>2. Tell Us About Your Video</h3>
            </Typography>
            <div className="space-y-4 px-1">
              <div className="space-y-6 sm:grid sm:grid-cols-3 sm:gap-6 sm:space-y-0">
                <FormInput
                  type="text"
                  name="VideoTitle"
                  label="Video title"
                  className="col-span-2"
                  disabled={formDisabled}
                />
                <FormDate name="VideoFilm" label="When did you film it?" disabled={formDisabled} />
              </div>
              <FormText
                type="text"
                name="VideoDescription"
                label="Tell us all about your video"
                disabled={formDisabled}
              />
              <div>
                <FormMultiSelect
                  name="VideoTags"
                  label="How should we tag it?"
                  placeholder="Choose tags..."
                  options={data.tags.map((tag) => ({ value: tag.MarqueeTagID, label: tag.Name }))}
                  disabled={formDisabled}
                />
                <Typography className="text-sm text-muted-foreground">
                  <p>
                    Consult our{" "}
                    <Link to="/the-licktionary-our-realworldsex-tags" target="_blank">
                      Licktionary
                    </Link>{" "}
                    as a guide on how to tag your video.
                  </p>
                </Typography>
              </div>
            </div>
          </div>
          <MusicSection formDisabled={formDisabled} containsMusic={containsMusic} setContainsMusic={setContainsMusic} />
          <div className="space-y-4">
            <Typography>
              <h3 className={cn(formDisabled && "opacity-50")}>3. Upload Your Video</h3>
            </Typography>
            <div className="space-y-4 px-1">
              <div className="space-y-6 sm:grid sm:grid-cols-3 sm:gap-6 sm:space-y-0">
                <FormFile
                  name="VideoIntro"
                  accept=".mp4,.webm,.ogv,.mov,.qt,.3g2,.3gp,.avi,.f4v,.flv,.m4v,.mkv,.mpg,.mpeg,.wmv"
                  sizeLimit={{ size: 5000000000, label: "5Gb" }}
                  label="Video Intro"
                  url={introUrl}
                  setUrl={setIntroUrl}
                  onSetDuration={(d) => console.log("intro duration", d)}
                  disabled={formDisabled}
                />
                <FormFile
                  name="VideoReal"
                  accept=".mp4,.webm,.ogv,.mov,.qt,.3g2,.3gp,.avi,.f4v,.flv,.m4v,.mkv,.mpg,.mpeg,.wmv"
                  sizeLimit={{ size: 5000000000, label: "5Gb" }}
                  label="Your #RealWorldSex Video"
                  url={videoUrl}
                  setUrl={setVideoUrl}
                  onSetDuration={(d) => console.log("duration", d)}
                  disabled={formDisabled}
                />
                <FormFile
                  name="MainStill"
                  accept=".jpg,.jpeg,.png,.gif"
                  sizeLimit={{ size: 5000000, label: "5mb" }}
                  label="Main Still"
                  url={mainStill}
                  setUrl={setMainStill}
                  disabled={formDisabled}
                />
              </div>
              <div className="space-y-6 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0">
                <FormFile
                  name="AdditionalStill1"
                  accept=".jpg,.jpeg,.png,.gif"
                  sizeLimit={{ size: 5000000, label: "5mb" }}
                  label="Additional Still"
                  url={still1}
                  setUrl={setStill1}
                  disabled={formDisabled}
                />
                <FormFile
                  name="AdditionalStill2"
                  accept=".jpg,.jpeg,.png,.gif"
                  sizeLimit={{ size: 5000000, label: "5mb" }}
                  label="Additional Still"
                  url={still2}
                  setUrl={setStill2}
                  disabled={formDisabled}
                />
              </div>
              <div className="space-y-6 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0">
                <FormFile
                  name="AdditionalStill3"
                  accept=".jpg,.jpeg,.png,.gif"
                  sizeLimit={{ size: 5000000, label: "5mb" }}
                  label="Additional Still"
                  url={still3}
                  setUrl={setStill3}
                  disabled={formDisabled}
                />
                <FormFile
                  name="AdditionalStill4"
                  accept=".jpg,.jpeg,.png,.gif"
                  sizeLimit={{ size: 5000000, label: "5mb" }}
                  label="Additional Still"
                  url={still4}
                  setUrl={setStill4}
                  disabled={formDisabled}
                />
              </div>
            </div>
          </div>

          <FormCheckbox name="Exclusivity" disabled={formDisabled}>
            <Typography>
              <p>This video is exclusive to makelovenotporn.tv, and is not crossposted to any other website.</p>
            </Typography>
          </FormCheckbox>
          <FormCheckbox name="Agreement" disabled={formDisabled}>
            <Typography>
              <p>
                I have read, understand, and agree to the{" "}
                <Link to="/video-submission-agreement" target="_blank">
                  video submission agreement
                </Link>
                .
              </p>
            </Typography>
          </FormCheckbox>
          <SubmitInterceptButton
            formDisabled={formDisabled}
            mainStill={mainStill}
            still1={still1}
            still2={still2}
            still3={still3}
            still4={still4}
            showStillsDialog={showStillsDialog}
            setShowStillsDialog={setShowStillsDialog}
            formRef={formRef}
          />
        </ValidatedForm>
      </div>
    </div>
  )
}

function MusicSection({
  formDisabled,
  containsMusic,
  setContainsMusic,
}: {
  formDisabled: boolean
  containsMusic: string
  setContainsMusic: (value: string) => void
}) {
  const { error: containsMusicError } = useField("ContainsMusic")
  const [controlValue, setControlValue] = useControlField<string>("ContainsMusic")

  function handleValueChange(value: string) {
    setContainsMusic(value)
    setControlValue(value)
  }

  return (
    <div className="space-y-4">
      <Typography>
        <h3 className={cn(formDisabled && "opacity-50")}>Does your video contain music?</h3>
      </Typography>
      <div className="space-y-4 px-1">
        <input type="hidden" name="ContainsMusic" value={controlValue ?? ""} />
        <RadioGroup onValueChange={handleValueChange} value={controlValue ?? ""} disabled={formDisabled}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="music-yes" disabled={formDisabled} />
            <label htmlFor="music-yes" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Yes</label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="music-no" disabled={formDisabled} />
            <label htmlFor="music-no" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">No</label>
          </div>
        </RadioGroup>
        {containsMusicError && <p className="text-sm text-destructive">{containsMusicError}</p>}
        {(controlValue === "yes" || containsMusic === "yes") && (
          <FormText
            name="MusicSource"
            label="Please tell us where you sourced the music so we can ensure it is copyright free"
            disabled={formDisabled}
          />
        )}
      </div>
    </div>
  )
}

function SubmitInterceptButton({
  formDisabled,
  mainStill,
  still1,
  still2,
  still3,
  still4,
  showStillsDialog,
  setShowStillsDialog,
  formRef,
}: {
  formDisabled: boolean
  mainStill: string
  still1: string
  still2: string
  still3: string
  still4: string
  showStillsDialog: boolean
  setShowStillsDialog: (open: boolean) => void
  formRef: React.RefObject<HTMLFormElement>
}) {
  const isSubmitting = useIsSubmitting("createVideo")

  function handleSubmitClick() {
    const stillsCount = [mainStill, still1, still2, still3, still4].filter((s) => s !== "").length
    if (stillsCount < 5) {
      setShowStillsDialog(true)
    } else {
      formRef.current?.requestSubmit()
    }
  }

  function handleContinueSubmission() {
    setShowStillsDialog(false)
    formRef.current?.requestSubmit()
  }

  return (
    <>
      <Button
        type="button"
        className="w-48"
        disabled={formDisabled || isSubmitting}
        onClick={handleSubmitClick}
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </Button>
      <Dialog open={showStillsDialog} onOpenChange={setShowStillsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Not all stills submitted</DialogTitle>
            <DialogDescription>
              You've submitted fewer than 5 stills. Our curation team will pick the remaining stills from the frames of your video if you choose to continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setShowStillsDialog(false)}>
              Add additional stills
            </Button>
            <Button onClick={handleContinueSubmission}>
              Continue Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function ParticipantForm({
  namePrefix,
  itemKey,
  label,
  remove,
  disabled,
}: {
  namePrefix: string
  itemKey: string
  label: string
  remove?: any
  disabled?: boolean
}) {
  const [id1, setID1] = useState("")
  const [id2, setID2] = useState("")
  return (
    <div key={itemKey} className="border-2 border-accent rounded-md">
      <Typography>
        <h4 className="flex justify-between items-center bg-accent py-4 px-4">
          <span className={cn(disabled && "opacity-50")}>{label}</span>
          {remove && (
            <Button className="text-foreground" variant="link" size="icon" onClick={remove}>
              <Cross1Icon />
            </Button>
          )}
        </h4>
      </Typography>
      <div className="p-4 pb-6 space-y-6">
        <div className="space-y-6 sm:grid sm:grid-cols-3 sm:gap-6 sm:space-y-0">
          <FormInput name={`${namePrefix}FirstName`} label="First name" disabled={disabled} />
          <FormInput name={`${namePrefix}LastName`} label="Last name" disabled={disabled} />
          <FormInput name={`${namePrefix}NickName`} label="Aliases, pseudonyms, etc" disabled={disabled} />
        </div>
        <div
          className={cn(
            "space-y-6 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0",
            itemKey !== "User" && "sm:grid-cols-3"
          )}>
          {itemKey !== "User" && (
            <FormInput name={`${namePrefix}Email`} type="email" label="Email address" disabled={disabled} />
          )}
          <FormDate name={`${namePrefix}Birthday`} label="Date of birth" disabled={disabled} />
          <FormSelect
            name={`${namePrefix}Country`}
            label="Country"
            placeholder="Select Country"
            options={Countries.map((country) => ({ value: country.code, label: country.label }))}
            disabled={disabled}
          />
        </div>
        <div className="space-y-6 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0">
          <FormFile
            name={`${namePrefix}IDImageOne`}
            label="Upload ID one*"
            accept=".jpg,.jpeg,.png,.gif"
            sizeLimit={{ size: 5000000, label: "5mb" }}
            url={id1}
            setUrl={setID1}
            disabled={disabled}
          />
          <FormFile
            name={`${namePrefix}IDImageTwo`}
            label="Upload ID two*"
            accept=".jpg,.jpeg,.png,.gif"
            sizeLimit={{ size: 5000000, label: "5mb" }}
            url={id2}
            setUrl={setID2}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}
